import { Medal } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useJobGroupClusters } from '@/api/queries/job-group-cluster-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	useExcludedClusters,
	useRanking,
	useUpdateExcludedClusters,
} from '@/api/queries/scoring-queries'
import { Button } from '@/components/ui/button'
import { MultiSelect } from '@/components/ui/multi-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { getMonthLabel, MONTH_NUMBERS } from '@/constants/months'
import { ROLE_LABELS } from '@/constants/role-labels'
import { cn } from '@/lib/utils'
import type { RankingEntry, RankingPeriodType, RankingScope } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import { RankingPersonDetailsDialog } from './ranking-person-details-dialog'

const ALL_CLUSTERS = 'all'

const PERIOD_TYPES: RankingPeriodType[] = [
	'MONTH',
	'QUARTER',
	'SEMESTER',
	'YEAR',
]
const MONTHS_PER_PERIOD: Record<RankingPeriodType, number> = {
	MONTH: 1,
	QUARTER: 3,
	SEMESTER: 6,
	YEAR: 12,
}

/** Índice (1-based) do período corrente: mês 1–12, trimestre 1–4, semestre 1–2. */
function currentPeriodIndex(periodType: RankingPeriodType, now = new Date()) {
	return Math.floor(now.getMonth() / MONTHS_PER_PERIOD[periodType]) + 1
}

const MEDAL_CLASS: Record<number, string> = {
	1: 'text-kaefer-honey-yellow',
	2: 'text-kaefer-silver',
	3: 'text-amber-700',
}

function formatScore(value: number) {
	return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

function PositionCard({
	label,
	entry,
	participants,
	onOpenDetails,
}: {
	label: string
	entry: RankingEntry | null
	participants: number
	onOpenDetails: (personId: string) => void
}) {
	const { t } = useTranslation()

	return (
		<button
			type="button"
			disabled={!entry}
			onClick={() => entry && onOpenDetails(entry.personId)}
			title={
				entry ? t('administration.scoring.ranking.details.open') : undefined
			}
			className="flex flex-1 flex-col gap-1 rounded-lg border border-kaefer-light-silver bg-white p-4 text-left transition-colors enabled:cursor-pointer enabled:hover:bg-kaefer-honey-yellow/10"
		>
			<span className="text-xs text-muted-foreground">{label}</span>
			{entry ? (
				<>
					<span className="text-2xl font-bold tabular-nums text-kaefer-dark-grey">
						{entry.position}º
						<span className="ml-1 text-sm font-normal text-muted-foreground">
							{t('administration.scoring.ranking.ofParticipants', {
								total: participants,
							})}
						</span>
					</span>
					<span className="text-xs text-muted-foreground">
						{t('administration.scoring.ranking.myScore', {
							score: formatScore(entry.score),
						})}
					</span>
				</>
			) : (
				<span className="text-sm text-muted-foreground">
					{t('administration.scoring.ranking.notRanked')}
				</span>
			)}
		</button>
	)
}

function ExcludedClustersEditor({
	clusterOptions,
}: {
	clusterOptions: { value: string; label: string }[]
}) {
	const { t } = useTranslation()
	const { data: savedIds } = useExcludedClusters()
	const updateMutation = useUpdateExcludedClusters()
	const [selected, setSelected] = useState<string[]>([])

	useEffect(() => {
		if (savedIds) setSelected(savedIds)
	}, [savedIds])

	const isDirty =
		!!savedIds &&
		(selected.length !== savedIds.length ||
			selected.some((id) => !savedIds.includes(id)))

	const handleSave = async () => {
		try {
			await updateMutation.mutateAsync(selected)
			toast.success(t('administration.scoring.ranking.excludedSaveSuccess'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<div className="flex flex-col gap-2 rounded-lg border border-dashed border-kaefer-light-silver p-3">
			<span className="text-xs text-muted-foreground">
				{t('administration.scoring.ranking.excludedHint')}
			</span>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<MultiSelect
					options={clusterOptions}
					values={selected}
					onValuesChange={setSelected}
					placeholder={t('administration.scoring.ranking.excludedPlaceholder')}
					className="sm:flex-1"
				/>
				<Button
					type="button"
					onClick={handleSave}
					disabled={!isDirty || updateMutation.isPending}
					className="bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 sm:w-auto"
				>
					{updateMutation.isPending ? (
						<>
							<Spinner /> {t('administration.scoring.saving')}
						</>
					) : (
						t('administration.scoring.save')
					)}
				</Button>
			</div>
		</div>
	)
}

export function RankingTab() {
	const { t, i18n } = useTranslation()
	const currentYear = new Date().getFullYear()
	const yearOptions = [currentYear - 2, currentYear - 1, currentYear]
	const [periodType, setPeriodType] = useState<RankingPeriodType>('MONTH')
	const [year, setYear] = useState(currentYear)
	const [periodIndex, setPeriodIndex] = useState(currentPeriodIndex('MONTH'))

	const changePeriodType = (value: RankingPeriodType) => {
		setPeriodType(value)
		setPeriodIndex(currentPeriodIndex(value))
	}

	// opções do 3º seletor conforme o tipo: meses, trimestres ou semestres
	const periodIndexOptions =
		periodType === 'MONTH'
			? MONTH_NUMBERS.map((m) => ({
					value: m,
					label: getMonthLabel(m, i18n.language),
				}))
			: Array.from({ length: 12 / MONTHS_PER_PERIOD[periodType] }, (_, i) => ({
					value: i + 1,
					label: t(`administration.scoring.ranking.periodIndex.${periodType}`, {
						index: i + 1,
					}),
				}))
	const [scope, setScope] = useState<RankingScope>('GENERAL')
	const [branchId, setBranchId] = useState<string>()
	const [crId, setCrId] = useState<string>()
	const [clusterId, setClusterId] = useState(ALL_CLUSTERS)

	const { data: branches } = useOrganization()
	const { data: clusters } = useJobGroupClusters()

	const branchOptions = useMemo(
		() => (branches ?? []).map((b) => ({ value: b.id, label: b.name })),
		[branches],
	)
	const crOptions = useMemo(
		() =>
			(branches ?? []).flatMap((b) =>
				b.crs.map((c) => ({ value: c.id, label: `${c.name} — ${b.name}` })),
			),
		[branches],
	)
	const clusterOptions = useMemo(
		() => (clusters ?? []).map((c) => ({ value: c.id, label: c.name })),
		[clusters],
	)

	const scopeReady =
		scope === 'GENERAL' ||
		(scope === 'BRANCH' && !!branchId) ||
		(scope === 'CR' && !!crId)

	const [detailsPersonId, setDetailsPersonId] = useState<string>()
	const periodParams = {
		periodType,
		year,
		...(periodType !== 'YEAR' && { periodIndex }),
	}

	const { data, isLoading, isFetching } = useRanking(
		{
			...periodParams,
			scope,
			...(scope === 'BRANCH' && { branchId }),
			...(scope === 'CR' && { crId }),
			...(clusterId !== ALL_CLUSTERS && { jobGroupClusterId: clusterId }),
		},
		scopeReady,
	)

	const items = data?.items ?? []

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<Select
					value={periodType}
					onValueChange={(value) =>
						changePeriodType(value as RankingPeriodType)
					}
				>
					<SelectTrigger className="w-full sm:w-36">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{PERIOD_TYPES.map((type) => (
							<SelectItem key={type} value={type}>
								{t(`administration.scoring.ranking.periodTypes.${type}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{periodType !== 'YEAR' && (
					<Select
						value={String(periodIndex)}
						onValueChange={(value) => setPeriodIndex(Number(value))}
					>
						<SelectTrigger className="w-full sm:w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{periodIndexOptions.map((option) => (
								<SelectItem key={option.value} value={String(option.value)}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}

				<Select
					value={String(year)}
					onValueChange={(value) => setYear(Number(value))}
				>
					<SelectTrigger className="w-full sm:w-28">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{yearOptions.map((value) => (
							<SelectItem key={value} value={String(value)}>
								{value}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
				<Select
					value={scope}
					onValueChange={(value) => setScope(value as RankingScope)}
				>
					<SelectTrigger className="w-full sm:w-40">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="GENERAL">
							{t('administration.scoring.ranking.scopes.general')}
						</SelectItem>
						<SelectItem value="BRANCH">
							{t('administration.scoring.ranking.scopes.branch')}
						</SelectItem>
						<SelectItem value="CR">
							{t('administration.scoring.ranking.scopes.cr')}
						</SelectItem>
					</SelectContent>
				</Select>

				{scope === 'BRANCH' && (
					<SearchableSelect
						options={branchOptions}
						value={branchId}
						onValueChange={setBranchId}
						placeholder={t('administration.scoring.ranking.selectBranch')}
						className="w-full sm:w-64"
					/>
				)}
				{scope === 'CR' && (
					<SearchableSelect
						options={crOptions}
						value={crId}
						onValueChange={setCrId}
						placeholder={t('administration.scoring.ranking.selectCr')}
						className="w-full sm:w-80"
					/>
				)}

				<Select value={clusterId} onValueChange={setClusterId}>
					<SelectTrigger className="w-full sm:w-56">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL_CLUSTERS}>
							{t('administration.scoring.ranking.allClusters')}
						</SelectItem>
						{clusterOptions.map((c) => (
							<SelectItem key={c.value} value={c.value}>
								{c.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{isFetching && <Spinner className="text-kaefer-medium-grey" />}
			</div>

			{scope === 'GENERAL' && clusterId === ALL_CLUSTERS && (
				<ExcludedClustersEditor clusterOptions={clusterOptions} />
			)}

			{data && (
				<div className="flex flex-col gap-3 sm:flex-row">
					<PositionCard
						label={t('administration.scoring.ranking.myFilteredPosition')}
						entry={data.me.filtered}
						participants={data.totalParticipants}
						onOpenDetails={setDetailsPersonId}
					/>
					<PositionCard
						label={t('administration.scoring.ranking.myOverallPosition')}
						entry={data.me.overall}
						participants={data.me.overallParticipants}
						onOpenDetails={setDetailsPersonId}
					/>
				</div>
			)}

			{!scopeReady ? (
				<p className="p-6 text-center text-sm text-muted-foreground">
					{t(
						scope === 'BRANCH'
							? 'administration.scoring.ranking.selectBranch'
							: 'administration.scoring.ranking.selectCr',
					)}
				</p>
			) : isLoading ? (
				<div className="p-6 text-center text-sm text-muted-foreground">
					{t('administration.scoring.loading')}
				</div>
			) : items.length === 0 ? (
				<p className="rounded-lg border border-dashed border-kaefer-light-silver p-6 text-center text-sm text-muted-foreground">
					{t('administration.scoring.ranking.empty')}
				</p>
			) : (
				<div className="rounded-lg border border-kaefer-light-silver">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-14">#</TableHead>
								<TableHead>
									{t('administration.scoring.ranking.person')}
								</TableHead>
								<TableHead className="hidden lg:table-cell">
									{t('administration.scoring.ranking.location')}
								</TableHead>
								<TableHead className="hidden md:table-cell">
									{t('administration.scoring.ranking.role')}
								</TableHead>
								<TableHead className="hidden text-right md:table-cell">
									{t('administration.scoring.ranking.inspections')}
								</TableHead>
								<TableHead className="hidden text-right md:table-cell">
									{t('administration.scoring.ranking.activities')}
								</TableHead>
								<TableHead className="hidden text-right sm:table-cell">
									{t('administration.scoring.ranking.rawScore')}
								</TableHead>
								<TableHead className="text-right">
									{t('administration.scoring.ranking.score')}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{items.map((entry) => {
								const isMe = entry.personId === data?.me.filtered?.personId
								return (
									<TableRow
										key={entry.personId}
										onClick={() => setDetailsPersonId(entry.personId)}
										title={t('administration.scoring.ranking.details.open')}
										className={cn(
											'cursor-pointer',
											isMe && 'bg-kaefer-honey-yellow/15',
										)}
									>
										<TableCell className="font-semibold tabular-nums text-kaefer-dark-grey">
											<span className="inline-flex items-center gap-1">
												{MEDAL_CLASS[entry.position] && (
													<Medal
														className={cn(
															'size-4',
															MEDAL_CLASS[entry.position],
														)}
													/>
												)}
												{entry.position}
											</span>
										</TableCell>
										<TableCell className="whitespace-normal">
											<div className="text-kaefer-dark-grey">{entry.name}</div>
											{entry.jobRole && (
												<div className="text-xs text-muted-foreground">
													{entry.jobRole}
												</div>
											)}
										</TableCell>
										<TableCell className="hidden whitespace-normal text-xs text-muted-foreground lg:table-cell">
											<div>{entry.branchName}</div>
											<div>{entry.crName}</div>
										</TableCell>
										<TableCell className="hidden text-xs md:table-cell">
											{ROLE_LABELS[entry.role] ?? entry.role}
											<span className="ml-1 text-muted-foreground">
												×{formatScore(entry.weight)}
											</span>
										</TableCell>
										<TableCell
											className="hidden text-right tabular-nums md:table-cell"
											title={t('administration.scoring.ranking.breakdown', {
												cartaoAmarelo: entry.breakdown.cartaoAmarelo,
												vereAgir: entry.breakdown.vereAgir,
												interacao: entry.breakdown.interacao,
											})}
										>
											{entry.inspections}
										</TableCell>
										<TableCell
											className="hidden text-right tabular-nums md:table-cell"
											title={t(
												'administration.scoring.ranking.activitiesBreakdown',
												{
													ddsAttended: entry.breakdown.ddsAttended,
													ddsMinistered: entry.breakdown.ddsMinistered,
													noticeRead: entry.breakdown.noticeRead,
													direcionada: entry.breakdown.direcionada,
												},
											)}
										>
											{entry.activities}
										</TableCell>
										<TableCell className="hidden text-right tabular-nums text-muted-foreground sm:table-cell">
											{formatScore(entry.rawScore)}
										</TableCell>
										<TableCell className="text-right font-semibold tabular-nums text-kaefer-dark-grey">
											{formatScore(entry.score)}
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</div>
			)}

			<RankingPersonDetailsDialog
				personId={detailsPersonId}
				params={periodParams}
				onClose={() => setDetailsPersonId(undefined)}
			/>
		</div>
	)
}
